'use client';

import { useEffect, useMemo, useState } from 'react';

import { RadioTile, RadioTiles, Text, TextLevels, TextMarkup, TypographyBold } from '@bytel/trilogy-react';

import { getDefaultPlan } from '@services/products';

import { MobilePlanType } from '@app-types/plan';

import { InlinePrice } from '@components/inline-price';

import { CategoryBox } from '@product-detail/components';
import { revalidateH2Counter } from '@product-detail/helpers';

interface WithOrWithoutPlanProps {
    plans: MobilePlanType[];
    currentPlan?: MobilePlanType;
    withoutPlanPrice: number;
    setCurrentPlan: (plan?: MobilePlanType) => void;
}

export function WithOrWithoutPlan({
    plans,
    currentPlan,
    withoutPlanPrice,
    setCurrentPlan,
}: Readonly<WithOrWithoutPlanProps>) {
    const [previousPlan, setPreviousPlan] = useState<MobilePlanType | undefined>(undefined);

    useEffect(() => {
        if (currentPlan !== undefined) {
            setPreviousPlan(currentPlan);
        }
    }, [currentPlan]);

    const handleWithPlan = () => {
        const defaultPlanGencode = getDefaultPlan(false).toString();
        const defaultPlan = plans.find((plan) => plan.gencode === defaultPlanGencode);

        const planToSet = previousPlan || defaultPlan;
        setCurrentPlan(planToSet);
        revalidateH2Counter();
    };

    const handleWithoutPlan = () => {
        if (currentPlan !== undefined) {
            setPreviousPlan(currentPlan);
        }
        setCurrentPlan(undefined);
        revalidateH2Counter();
    };

    const minimumPrice = useMemo(() => {
        return getMinimumPhonePrice(plans);
    }, [plans]);

    return (
        <CategoryBox title='Avec ou sans forfait&nbsp;?' formId='withPlans'>
            <RadioTiles>
                <RadioTile
                    id='withPlan'
                    horizontal
                    label='Avec forfait'
                    description={
                        minimumPrice < Infinity ? (
                            <>
                                <Text markup={TextMarkup.SPAN}>Prix du téléphone&nbsp;: </Text>
                                <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                    à partir de <InlinePrice level={TextLevels.TWO} amount={minimumPrice} />
                                </Text>
                            </>
                        ) : undefined
                    }
                    name='plan'
                    onChange={handleWithPlan}
                    checked={currentPlan !== undefined}
                />
                <RadioTile
                    id='noPlan'
                    horizontal
                    label='Sans forfait'
                    description={
                        <>
                            <Text markup={TextMarkup.SPAN}>Prix du téléphone&nbsp;: </Text>

                            <InlinePrice level={TextLevels.TWO} amount={withoutPlanPrice} />
                        </>
                    }
                    name='plan'
                    onChange={handleWithoutPlan}
                    checked={currentPlan === undefined}
                />
            </RadioTiles>
        </CategoryBox>
    );
}

const getMinimumPhonePrice = (plans: MobilePlanType[]) => {
    return plans
        .map((p) => p.simulatedCart?.quotes[0]?.products[0]?.details?.price.final || Infinity)
        .reduce((min, price) => Math.min(min, price), Infinity);
};
